import { useCallback, useMemo } from 'react';

import type {
  Checkpoint,
  GateKind,
  SchedulingGate,
  UpdateSchedulingGateRequest,
} from '../types';

import {
  useAvailabilityRulesQuery,
  useCreateAvailabilityRuleMutation,
  useCreateSchedulingGateMutation,
  useDeleteAvailabilityRuleMutation,
  useDeleteSchedulingGateMutation,
  useSchedulingGatesQuery,
  useUpdateSchedulingGateMutation,
} from './api';

/**
 * Booking-gate orchestration for the funnel settings UI. Indexes org-wide gates
 * by kind (per-encounter-type overrides are a fast-follow) and exposes the
 * add / move-and-edit / remove primitives the section composes into the funnel
 * model — adding a gate to a section sets its checkpoint, moving patches it.
 */
export function useBookingGates() {
  const { data, isLoading, isFetching, error, refetch } =
    useSchedulingGatesQuery();
  const [createGate] = useCreateSchedulingGateMutation();
  const [updateGate] = useUpdateSchedulingGateMutation();
  const [deleteGate] = useDeleteSchedulingGateMutation();

  const gatesByKind = useMemo(() => {
    const map: Partial<Record<GateKind, SchedulingGate>> = {};
    for (const gate of data?.items ?? []) {
      // Org-wide only in this UI (encounter_type_id null).
      if (gate.encounter_type_id == null) {
        map[gate.gate] = gate;
      }
    }
    return map;
  }, [data]);

  /** Create a gate at a specific funnel checkpoint. `mode` is only used by
   * eligibility (enrichment) today; card/paperwork take the backend default. */
  const addGate = useCallback(
    (gate: GateKind, checkpoint: Checkpoint, options?: { mode?: string }) =>
      createGate({
        createSchedulingGateRequest: {
          gate,
          checkpoint,
          enabled: true,
          ...(options?.mode ? { mode: options.mode } : {}),
        },
      }).unwrap(),
    [createGate],
  );

  const updateGateById = useCallback(
    (policyId: string, patch: UpdateSchedulingGateRequest) =>
      updateGate({ policyId, updateSchedulingGateRequest: patch }).unwrap(),
    [updateGate],
  );

  const removeGate = useCallback(
    (policyId: string) => deleteGate({ policyId }).unwrap(),
    [deleteGate],
  );

  return {
    gatesByKind,
    isLoading,
    isFetching,
    error,
    refetch,
    addGate,
    updateGateById,
    removeGate,
  };
}

/**
 * Availability rules for one calendar owner (defaults to the caller's own).
 * `perPage` is generous since a practice's rule set is small.
 */
export function useAvailabilityRules(providerId?: string | null) {
  const { data, isLoading, isFetching, error, refetch } =
    useAvailabilityRulesQuery({
      providerId: providerId ?? undefined,
      perPage: 200,
    });
  const [createRuleMutation] = useCreateAvailabilityRuleMutation();
  const [deleteRuleMutation] = useDeleteAvailabilityRuleMutation();

  const createRule = useCallback(
    (createAvailabilityRuleRequest: Parameters<
      typeof createRuleMutation
    >[0]['createAvailabilityRuleRequest']) =>
      createRuleMutation({ createAvailabilityRuleRequest }).unwrap(),
    [createRuleMutation],
  );

  const removeRule = useCallback(
    (availabilityRuleId: string) =>
      deleteRuleMutation({ availabilityRuleId }).unwrap(),
    [deleteRuleMutation],
  );

  return {
    rules: data?.items ?? [],
    isLoading,
    isFetching,
    error,
    refetch,
    createRule,
    removeRule,
  };
}
